import React from "react";

import { parse } from "cookie";
import { createRoot, hydrateRoot } from "react-dom/client";
import { v4 as uuidv4 } from "uuid";

import { initUFO } from "@/analytics/react-ufo/initUFO";
import { getAnalyticsWebClient } from "@/analytics/wac-analytics/get-analytics-web-client";
import {
    IntercomProperties,
    IntercomSettingsProperties
} from "@/libraries/intercom/src";
import { experiments } from "@/utils/experiment";
import { featureFlags } from "@/utils/feature-flags/feature-flag-manifest";
import { isPerformanceOnetrustEnabled } from "@/utils/onetrust-provider";

import { TemplateCategory } from "./components/confluence/templates/template-categories-search";
import {
    TemplateCollection,
    type Template
} from "./content/templates/template.types";
import { type AppPricingData } from "./contentful";
import { initializeFeatureGatesClient } from "./feature-gates/initialization";
import { Layout } from "./layout";
import {
    AvailableProductsResponse,
    ServerSideRequestStatus,
    UserProfile,
    UserProfileResponse
} from "./libraries/bxp-web-stargate-client/src";
import { GrowthUnifiedProfile } from "./libraries/growth-unified-profile-client";
import { PageWrapper } from "./page-wrapper";
import { AnchorContentsResponse } from "./utils/anchor-queries/get-anchor-contents";
import {
    overrideAjsAnonIdFromCookie,
    overrideCustomAttributes,
    isEdgeCachingPath
} from "./utils/fg-bootstrap-values-override-utils";
import { GridOverlayFunction } from "./utils/grid-overlay";
import { SentryIntlProvider } from "./utils/sentry-intl";
import { UserLocale } from "./utils/useGeoCurrency";
import {
    getExperimentOverrides,
    getFeatureGateOverrides
} from "./utils/wac-browser-utility";
import { parseRoute } from "./wac-routes";

window.BUILD_KEY = process.env["VERSION"];
window.featureGateBootstrapValues = window.featureGateBootstrapValues || {
    identifiers: {
        analyticsAnonymousId: uuidv4()
    }
};

declare global {
    type FeatureFlagType = "boolean" | "string" | "number";

    interface Window {
        IS_STATIC?: boolean;
        BUILD_KEY?: string;
        entryName?: string;
        SSR_languagePack: Record<string, string>;
        prices?: AppPricingData;
        availableProducts?: AvailableProductsResponse;
        userProfile?: UserProfile;
        userProfileStatus?: ServerSideRequestStatus;
        userContext?: UserProfileResponse;
        userContextStatus?: ServerSideRequestStatus;
        loggedOutContext?: GrowthUnifiedProfile;
        loggedOutContextStatus?: ServerSideRequestStatus;
        cmsPageVariant?: string;
        customersLanding?: CustomersLanding;
        templates?: Template[];
        mlTemplateIds?: string[];
        partnerTemplates?: Template[];
        collections?: TemplateCollection[];
        categories?: TemplateCategory[];
        teamType?: string;
        keyword?: string;
        featureFlags: Record<string, FeatureFlagType>;
        countryCode?: UserLocale;
        AtlOneTrustWrapper?: { updatePolicyLinks: () => void };
        Intercom?: IntercomProperties;
        intercomSettings?: IntercomSettingsProperties;
        toggleGrid?: GridOverlayFunction;
        isUniformPlayground?: boolean;
        s2sExperimentCallMade?: boolean;
        referrer?: string;
        track?: string;
        useEsmodules?: boolean;
        anchor?: string;
        subAnchor?: string;
        auxiaResponse?: AnchorContentsResponse | null;
        ssrAnonId?: string;
    }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const init = (Component: React.ComponentType<any>) => {
    const isStatic = typeof window !== "undefined" && window.IS_STATIC;
    const analyticsWebClient = getAnalyticsWebClient();
    const { locale, path } = parseRoute(window.location);
    isPerformanceOnetrustEnabled() && initUFO(analyticsWebClient, path);

    const parsedRequestCookies = document.cookie ? parse(document.cookie) : {};
    const experimentOverrides = getExperimentOverrides(
        parsedRequestCookies as Record<string, string>
    );
    const featureGateOverrides = getFeatureGateOverrides(
        parsedRequestCookies as Record<string, string>
    );

    const userAgent = window.navigator.userAgent;
    const pollinatorCheck = userAgent.includes("Pollinator");

    let finalExperimentOverrides = experimentOverrides;
    let finalFeatureGateOverrides = featureGateOverrides;

    if (pollinatorCheck) {
        finalExperimentOverrides = Object.values(experiments).reduce(
            (acc, experiment) => {
                acc[experiment.experimentName] = {
                    [experiment.parameterName]: experiment.defaultValue
                };
                return acc;
            },
            {} as Record<string, Record<string, boolean | number | string>>
        );
        finalFeatureGateOverrides = Object.entries(featureFlags).reduce(
            (acc, [, featureFlagObj]) => {
                acc[featureFlagObj.featureFlagName] = false;
                return acc;
            },
            {} as Record<string, boolean>
        );
    }

    // Below is for debugging edge - will be removed
    window.ssrAnonId =
        window.featureGateBootstrapValues.identifiers.analyticsAnonymousId;

    // We can't feature gate below code as this is done before initialization so just overriding for the path we are testing
    if (isEdgeCachingPath(window.location.pathname)) {
        // Override for Edge Caching
        overrideAjsAnonIdFromCookie();
        overrideCustomAttributes();
    }

    initializeFeatureGatesClient(window.featureGateBootstrapValues, {
        overrideValues: {
            experimentOverrides: finalExperimentOverrides,
            featureGateOverrides: finalFeatureGateOverrides
        }
    }).then(() => {
        if (isStatic) {
            // @ts-ignore
            const root = createRoot(document.getElementById("wac"));
            root.render(
                <SentryIntlProvider
                    locale={locale}
                    messages={window.SSR_languagePack}
                    cookies={document.cookie}
                >
                    <PageWrapper
                        analyticsWebClient={analyticsWebClient}
                        pureClientMode={true}
                        location={window.location}
                        pricing={window.prices}
                        availableProducts={window.availableProducts}
                        userProfile={window.userProfile}
                        userProfileStatus={window.userProfileStatus}
                        userContext={window.userContext}
                        userContextStatus={window.userContextStatus}
                        loggedOutContext={window.loggedOutContext}
                        loggedOutContextStatus={window.loggedOutContextStatus}
                        countryCode={window.countryCode}
                        templates={window.templates}
                        partnerTemplates={window.partnerTemplates}
                        collections={window.collections}
                        categories={window.categories}
                        composition={window._uniformPreloadedComposition}
                        isUniformPlayground={window.isUniformPlayground}
                        teamType={window.teamType}
                        keyword={window.keyword}
                        referrer={window.referrer}
                        mlTemplateIds={window.mlTemplateIds}
                        track={window.track}
                        anchor={window.anchor}
                        subAnchor={window.subAnchor}
                        auxiaResponse={window.auxiaResponse}
                        userAgent={userAgent}
                    >
                        {Component}
                    </PageWrapper>
                </SentryIntlProvider>
            );
        } else {
            hydrateRoot(
                // @ts-ignore
                document.getElementById("wac-root"),
                <SentryIntlProvider
                    locale={locale}
                    messages={window.SSR_languagePack}
                    cookies={document.cookie}
                >
                    <Layout
                        analyticsWebClient={analyticsWebClient}
                        location={window.location}
                        pricing={window.prices}
                        availableProducts={window.availableProducts}
                        tutorial={window.tutorial}
                        templates={window.templates}
                        partnerTemplates={window.partnerTemplates}
                        collections={window.collections}
                        categories={window.categories}
                        userProfile={window.userProfile}
                        userProfileStatus={window.userProfileStatus}
                        userContext={window.userContext}
                        userContextStatus={window.userContextStatus}
                        loggedOutContext={window.loggedOutContext}
                        loggedOutContextStatus={window.loggedOutContextStatus}
                        countryCode={window.countryCode}
                        composition={window._uniformPreloadedComposition}
                        isUniformPlayground={window.isUniformPlayground}
                        teamType={window.teamType}
                        keyword={window.keyword}
                        mlTemplateIds={window.mlTemplateIds}
                        referrer={window.referrer}
                        anchor={window.anchor}
                        subAnchor={window.subAnchor}
                        auxiaResponse={window.auxiaResponse}
                    >
                        {Component}
                    </Layout>
                </SentryIntlProvider>,
                {
                    onRecoverableError: (error, errorInfo) => {
                        // add an attribute if in CI
                        if (process.env["CI"] === "true") {
                            document
                                .getElementById("wac-root")
                                ?.setAttribute("hydration-error", "true");
                        }
                        if (process.env["MODE"] === "dev") {
                            console.log(
                                `Recoverable Error (including Hydration): ${error}`,
                                errorInfo
                            );
                        }
                    }
                }
            );
        }
    });
};
